import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import StudyService from "../../../service/study.service";
import AuthService from "../../../service/auth.service";
import {sanitizeStudyHtml} from "../../../utils/sanitizeStudyHtml";
import "./MockPrintView.css";


export default function MockPrintView() {
  const { paperId } = useParams();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printMode, setPrintMode] = useState(null);

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        const admin = AuthService.getCurrentUser()?.roles?.some(role => ['ROLE_ADMIN','ROLE_SUPER_ADMIN'].includes(role));
        const loadPaper = id => admin ? StudyService.getEditorPaper(id) : StudyService.getPaper(id);
        if (paperId === "multi") {
          const params = new URLSearchParams(window.location.search);
          const ids = (params.get("ids") || "").split(",").filter(id => id.trim() !== "");
          if (ids.length === 0) {
            setError("No papers selected");
            setLoading(false);
            return;
          }
          const results = await Promise.all(ids.map(loadPaper));
          const validPapers = results.map(res => res.data?.data).filter(p => p);
          if (validPapers.length > 0) {
            setPapers(validPapers);
          } else {
            setError("Papers not found");
          }
        } else {
          const res = await loadPaper(paperId);
          const data = res.data?.data;
          if (data) {
            setPapers([data]);
          } else {
            setError("Paper not found");
          }
        }
      } catch (err) {
        setError("Failed to load papers");
      } finally {
        setLoading(false);
      }
    };
    fetchPapers();
  }, [paperId]);

  useEffect(() => {
    // When papers are loaded and printMode is selected, trigger print
    if (papers.length > 0 && !loading && printMode) {
      // Small timeout to allow images and fonts to render
      const timer = setTimeout(() => {
        window.print();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [papers, loading, printMode]);

  if (loading) return <div style={{ padding: 20 }}>Loading printable view...</div>;
  if (error) return <div style={{ padding: 20, color: 'red' }}>{error}</div>;
  if (papers.length === 0) return null;
  const hasSolutions = papers.every(paper => (paper.sections || []).some(section => (section.questions || []).some(question => question.correctOption)));

  if (!printMode) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: 'sans-serif', background: '#f8fafc', minHeight: '100vh' }}>
        <h2 style={{ fontSize: 28, marginBottom: 10, color: '#1e293b' }}>What would you like to print?</h2>
        <p style={{ color: '#64748b', marginBottom: 40, fontSize: 16 }}>Select an option below to generate your customized PDF layout.</p>
        
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setPrintMode('questions')}
            style={{ padding: '16px 24px', fontSize: 16, borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontWeight: 600 }}
          >
            📄 Questions Only
          </button>
          
          {hasSolutions && <button 
            onClick={() => setPrintMode('answers')}
            style={{ padding: '16px 24px', fontSize: 16, borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontWeight: 600 }}
          >
            ✅ Answer Key & Solutions
          </button>}
          
          {hasSolutions && <button 
            onClick={() => setPrintMode('both')}
            style={{ padding: '16px 24px', fontSize: 16, borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontWeight: 600 }}
          >
            📚 Both (Questions + Answers)
          </button>}
        </div>
      </div>
    );
  }

  return (
    <div className="mock-print-container print-area">
      {papers.map((paper, paperIndex) => {
        // Flatten all questions from all sections for this paper
        let allQuestions = [];
        if (paper.sections && paper.sections.length > 0) {
          paper.sections.forEach(sec => {
            const qs = sec.questions || (paper.questions || []).filter(q => String(q.sectionId) === String(sec.id));
            allQuestions = allQuestions.concat(qs);
          });
        } else if (paper.questions && paper.questions.length > 0) {
          allQuestions = paper.questions;
        }

        return (
          <div key={paper.id || paperIndex} className="mock-print-paper-section" style={{ pageBreakBefore: paperIndex > 0 ? 'always' : 'auto', breakBefore: paperIndex > 0 ? 'page' : 'auto' }}>
            {/* Header */}
            <div className="mock-print-header">
              <h1>{paper.name}</h1>
              <p>Total Questions: {allQuestions.length} | Time: {paper.totalTimeMinutes} Minutes</p>
            </div>

            {/* Questions Section - Two Columns */}
            {(printMode === 'questions' || printMode === 'both') && (
              <div className="mock-print-questions">
              {allQuestions.map((q, idx) => (
                <div key={q.id || idx} className="mock-print-qbox">
                  <div className="mock-print-qtext">
                    <div className="q-label">Q{idx + 1}.</div>
                    <div className="q-content">
                      <div dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(q.questionText) }} />
                      {q.questionTextHindi && (
                        <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(q.questionTextHindi) }} />
                      )}
                    </div>
                  </div>
                  
                  <div className="mock-print-options">
                    {['A', 'B', 'C', 'D'].map(opt => {
                      const optKey = `option${opt}`;
                      const optHiKey = `option${opt}Hindi`;
                      const optText = q[optKey];
                      const optHiText = q[optHiKey];
                      
                      if (!optText && !optHiText) return null;

                      return (
                        <div key={opt} className="mock-print-opt">
                          <div className="opt-label"><strong>({opt})</strong></div>
                          <div className="opt-content">
                            {optText && <div dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(optText) }} />}
                            {optHiText && (
                              <div style={{ marginTop: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(optHiText) }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              </div>
            )}

            {/* Answer Key & Explanations Section - New Page */}
            {(printMode === 'answers' || printMode === 'both') && (
              <div className="mock-print-answers-page" style={(printMode === 'answers' && paperIndex === 0) ? { pageBreakBefore: 'auto', breakBefore: 'auto' } : {}}>
              <h2>Answer Key & Explanations {papers.length > 1 ? `- ${paper.name}` : ''}</h2>
              
              <div className="mock-print-answers-list">
                {allQuestions.map((q, idx) => {
                  const correctOpt = q.correctOption;
                  const expEn = q.explanation;
                  const expHi = q.explanationHindi;
                  
                  return (
                    <div key={q.id || idx} className="mock-print-answer-item">
                      <div className="correct-ans">
                        Q{idx + 1}. Correct Answer: ({correctOpt})
                      </div>
                      
                      {(expEn || expHi) && (
                        <div className="explanation">
                          <strong>Explanation:</strong>
                          {expEn && <div style={{ marginTop: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(expEn) }} />}
                          {expHi && <div style={{ marginTop: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeStudyHtml(expHi) }} />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
